import { Box, Dialog, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import CloseIcon from "@mui/icons-material/Close";
import InstallmentsTable from '../../pages/BillingAndCollection/Installments/InstallmentsTable';
import DueDateExtensionTable from '../../pages/BillingAndCollection/DueDateExtentsion/DueDateExtensionTable';
import Newconnectiontable from '../../pages/GeneralServices/NewConnection/NewConnectionTable';
import { convertDatewithTime } from '../../services/Functions';
import { getApplicationProgressStatus } from '../../utils/applicationProgressStatus';
import ChangeLoadTable from '../../pages/GeneralServices/ChangeOfLoad/ChangeOfLoadTable';
import ChangeAttributesTable from '../../pages/GeneralServices/ChangeOfAttribute/ChangeAttributesTable';
import ChangeTariffTable from '../../pages/GeneralServices/ChangeOfTariff/ChangeOfTariffTable';
import MeterChangeRequestTable from '../../pages/BillingAndCollection/MeterChangeRequest/MeterChangeRequestTable';
import Reconnectiontable from '../../pages/GeneralServices/Reconnection/ReconnectionTable';
import AdjustLastBillTable from '../../pages/BillingAndCollection/AdjustLastBill/AdjustLastBillTable';
import AmendBill2636Table from '../../pages/BillingAndCollection/AmendBill2636/AmendBill2636Table';
import { useSelector } from 'react-redux';

/**
 * SearchResultsPopup component displays search results in a dialog popup.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.openDialogOfApplication - Determines if the popup is visible
 * @param {Function} props.handleDialogOfApplicationClose - Callback function to close the popup
 * @param {number} props.selectedTab - The currently selected tab index
 * @param {number} props.selectedValueOfTab - The value of the selected tab
 * @param {Object[]} props.resultSearchedData - Array of primary search results
 * @param {Object[]} props.resultSearchedData1 - Array of secondary search results
 * @returns {JSX.Element|null} The search results popup component
 */
const SearchResultsPopup = ({ openDialogOfApplication, handleDialogOfApplicationClose, selectedTab, selectedValueOfTab, resultSearchedData, resultSearchedData1, setViewBackdrop }) => {
    const installmentsTableRef = useRef();
    const dueDateTableRef = useRef();
    const newConnectionTableRef = useRef();
    const changeOfLoadTableRef = useRef();
    const changeOfAttributesTableRef = useRef();
    const changeOfTariffTableRef = useRef();
    const meterChangeRequestTableRef = useRef();
    const reconnectionTableRef = useRef();
    const adjustLastBillTableRef = useRef();
    const partPaymentTableRef = useRef();
    const [applicantName, setApplicantName] = useState('');
    const [mergeState, setMergeState] = useState([])

    const { token, userId, hierarchy, roleId, roleName, permissions, code, roleTypeId } = useSelector((state) => state.auth)
    const [allMaterialList, setAllMaterialList] = useState([]);

    const hasHandledRowClick = useRef(false);
    const prevDataLength = useRef(0);
    // Keep service tables mounted after first search so closing this dialog does not tear down an open workflow popup.
    const [serviceTablesMounted, setServiceTablesMounted] = useState(false);

    useEffect(() => {
        if (openDialogOfApplication) {
            setServiceTablesMounted(true);
        }
    }, [openDialogOfApplication]);

    const waitForTableRef = async (tableRef, maxAttempts = 30) => {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            if (tableRef.current?.handleRowClick) {
                return tableRef.current;
            }
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return null;
    };

    const openApplicationFromSearch = useCallback(async (params) => {
        const typeId = Number(params.applicationTypeId);

        if (typeId === 1) {
            const table = await waitForTableRef(newConnectionTableRef);
            if (!table) throw new Error('New Connection table not ready');
            return table.handleRowClick(params.applicationId, params.status);
        }
        if (typeId === 2 || typeId === 5) {
            const table = await waitForTableRef(meterChangeRequestTableRef);
            if (!table) throw new Error('Meter Change Request table not ready');
            return table.handleRowClick(params.applicationId, params.status);
        }
        if (typeId === 15) {
            const table = await waitForTableRef(changeOfAttributesTableRef);
            if (!table) throw new Error('Change of Attributes table not ready');
            return table.handleRowClick(params.applicationId);
        }
        if (typeId === 4) {
            const table = await waitForTableRef(changeOfTariffTableRef);
            if (!table) throw new Error('Change of Tariff table not ready');
            return table.handleRowClick(params.applicationId, params.status);
        }
        if (typeId === 6) {
            const table = await waitForTableRef(changeOfLoadTableRef);
            if (!table) throw new Error('Change of Load table not ready');
            return table.handleRowClick(params.applicationId);
        }
        if (typeId === 7) {
            const table = await waitForTableRef(dueDateTableRef);
            if (!table) throw new Error('Due Date Extension table not ready');
            return table.handleRowClick(params.applicationId, params.status);
        }
        if (typeId === 8) {
            const table = await waitForTableRef(installmentsTableRef);
            if (!table) throw new Error('Installments table not ready');
            return table.handleRowClick(params.applicationId, params.status);
        }
        if (typeId === 13) {
            const table = await waitForTableRef(reconnectionTableRef);
            if (!table) throw new Error('Reconnection table not ready');
            return table.handleRowClick(params.applicationId, params.status);
        }
        if (typeId === 16) {
            const table = await waitForTableRef(adjustLastBillTableRef);
            if (!table) throw new Error('Adjust Last Bill table not ready');
            return table.handleRowClick(params.applicationId, params.status);
        }
        if (typeId === 17) {
            const table = await waitForTableRef(partPaymentTableRef);
            if (!table) throw new Error('Part Payment table not ready');
            return table.handleRowClick(params.applicationId, params.status);
        }

        return null;
    }, []);

    useEffect(() => {
        if (!openDialogOfApplication) {
            hasHandledRowClick.current = false;
            prevDataLength.current = 0;
            setMergeState([]);
            setApplicantName('');
            return;
        }

        const data1 = resultSearchedData || [];
        const data2 = resultSearchedData1 || [];
        const merged = [...data1, ...data2];
        const currentLength = merged.length;

        if (currentLength === 0) {
            setViewBackdrop(false);
            return;
        }

        if (currentLength === prevDataLength.current) {
            return;
        }
        prevDataLength.current = currentLength;

        setMergeState(merged);

        const namedRow = merged.find((item) => item.applicantName || item.name);
        if (namedRow) {
            setApplicantName(namedRow.applicantName || namedRow.name || '');
        }

        if (currentLength > 1) {
            setViewBackdrop(false);
            return;
        }

        if (currentLength === 1 && !hasHandledRowClick.current) {
            hasHandledRowClick.current = true;

            const openSingleResult = async () => {
                try {
                    await openApplicationFromSearch(merged[0]);
                } catch (error) {
                    console.error('Failed to auto-open application from search:', error);
                } finally {
                    setViewBackdrop(false);
                }
            };

            openSingleResult();
        }
    }, [
        openDialogOfApplication,
        resultSearchedData,
        resultSearchedData1,
        openApplicationFromSearch,
        handleDialogOfApplicationClose,
        setViewBackdrop,
    ]);

    /**
     * Handles row click event and triggers the appropriate table method.
     *
     * @param {Object} params - The selected row data
     * @param {number} params.applicationTypeId - ID representing the application type
     * @param {number} params.applicationId - ID of the selected application
     */


    //     try {
    //         const myHeaders = new Headers();
    //         myHeaders.append("Authorization", token);
    //         myHeaders.append("Content-Type", "application/json");

    //         const raw = JSON.stringify({
    //             // subDivisionCode: dialogRowData.applicationId.slice(3, 8)
    //             // subDivisionCode: '15175'
    //             subDivisionCode: applicationId.slice(3, 8)

    //         });

    //         const requestOptions = {
    //             method: "POST",
    //             headers: myHeaders,
    //             body: raw,
    //             redirect: "follow",
    //         };

    //         const response = await fetch(cfpBaseUrl + "enc/getMaterialDetails", requestOptions);
    //         const result = await response.json();
    //         console.log(result , 'result material')
    //         setAllMaterialList(result.data);

    //         return result.data;
    //     } catch (e) {
    //         console.log("Error in callMaterials:", e);
    //         return [];
    //     }
    // };



    const handleRowClick = async (params) => {
        setViewBackdrop(true);
        try {
            await openApplicationFromSearch(params);
        } catch (error) {
            console.error('Failed to open application from search:', error);
        } finally {
            setViewBackdrop(false);
        }
    };

    return (
        <>
            <Dialog
                open={openDialogOfApplication}
                onClose={handleDialogOfApplicationClose}
                maxWidth="xl"
                fullWidth
                disableEnforceFocus
                sx={{ width: '1600px', maxWidth: '60%', margin: '0 auto' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(180deg, #1DB6E8, #003971)', padding: '0px 16px' }}>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <DialogTitle style={{ color: 'white', margin: 0 }}>
                            Search Results For (
                            {selectedTab === 0
                                ? 'Reference No'
                                : selectedTab === 1
                                    ? 'Application ID'
                                    : selectedTab === 2
                                        ? 'CNIC'
                                        : selectedTab === 3
                                            ? 'Name'
                                            : ''} - {selectedValueOfTab}
                            {selectedTab !== 3 && applicantName ? ` | ${applicantName}` : ''}
                            )
                        </DialogTitle>

                    </div>
                    <IconButton onClick={handleDialogOfApplicationClose} style={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </div>

                {mergeState.length > 0 ?
                    <DialogContent sx={{ backgroundColor: '#f3f5f6', maxHeight: '400px', overflowY: 'auto' }}>
                        <Box>
                            <TableContainer component={Paper} sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <Table
                                    sx={{
                                        minWidth: 550,
                                        borderRadius: '8px',
                                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {selectedTab !== 1 && (
                                                <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '8px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Application No. / Ticket No.</TableCell>
                                            )}
                                            <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '10px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Date</TableCell>
                                            {selectedTab !== 2 && (
                                                <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '10px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }} >  Applicant CNIC </TableCell>
                                            )}
                                            {selectedTab !== 0 &&
                                                // <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '10px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Reference No.</TableCell>
                                                <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '10px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Tracking ID.</TableCell>

                                            }
                                            {selectedTab !== 3 &&
                                                <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '10px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Applicant Name</TableCell>
                                            }
                                            <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '10px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Type</TableCell>
                                            <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '10px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Source</TableCell>
                                            <TableCell sx={{ fontFamily: 'Roboto-Medium', backgroundColor: '#f5f5f5', padding: '10px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {mergeState.map((row, index) => (
                                            <TableRow key={index} onClick={() => handleRowClick(row)} sx={{ '&:hover': { cursor: 'pointer', backgroundColor: '#f0f0f0' } }}>
                                                {
                                                    selectedTab !== 1 && (
                                                        <TableCell sx={{ padding: '10px', textAlign: 'center' }}>{row.eOpsTrackingId}{row.applicationId}</TableCell>)
                                                }
                                                <TableCell sx={{ padding: '10px', textAlign: 'center' }}> {row.creationDate && row.creationDate} {row.registeredAt && convertDatewithTime(row.registeredAt)}</TableCell>
                                                {
                                                    selectedTab !== 2 &&
                                                    <TableCell sx={{ padding: '10px', textAlign: 'center' }}>{row.cnic || row.applicantCNIC ? (row.cnic || row.applicantCNIC) : '-'}</TableCell>
                                                }
                                                {
                                                    selectedTab !== 0 &&
                                                    <TableCell sx={{ padding: '10px', textAlign: 'center' }}>{row.applicationId}</TableCell>
                                                }
                                                {
                                                    selectedTab !== 3 &&
                                                    <TableCell sx={{ padding: '10px', textAlign: 'center' }}>{row.name || row.applicantName ? (row.name || row.applicantName) : '-'}</TableCell>
                                                }
                                                <TableCell sx={{ padding: '10px', textAlign: 'center' }}>{row.applicationTypeName}{row.complaintType}</TableCell>
                                                {/* <TableCell sx={{ padding: '10px', textAlign: 'center' }}>{(row.submittedThrough || row.source) ? (row.submittedThrough || row.source) : '-'}</TableCell> */}
                                                <TableCell sx={{ padding: '10px', textAlign: 'center' }}>{row.submittedThrough ? row.submittedThrough : '-'}</TableCell>
                                                <TableCell sx={{ padding: '10px', textAlign: 'center' }}>
                                                    {getApplicationProgressStatus(
                                                        row.applicationTypeId,
                                                        row.applicationProgressId,
                                                        row.objectionStatus,
                                                        row.status,
                                                    )}
                                                </TableCell>

                                                {/* <TableCell sx={{ padding: '10px', textAlign: 'center' }}>{row.status ? row.status : '-'}</TableCell> */}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </DialogContent> :
                    <>
                        <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', padding: '10px' }}>
                            Data not found.
                        </Typography>
                    </>}

            </Dialog>

            {serviceTablesMounted && (
                <>
                    <InstallmentsTable ref={installmentsTableRef} showTable={false} />
                    <DueDateExtensionTable ref={dueDateTableRef} showTable={false} />
                    <Newconnectiontable ref={newConnectionTableRef} showTable={false} />
                    <ChangeLoadTable ref={changeOfLoadTableRef} showTable={false} />
                    <ChangeAttributesTable
                        ref={changeOfAttributesTableRef}
                        showTable={false}
                    />
                    <ChangeTariffTable ref={changeOfTariffTableRef} showTable={false} />
                    <MeterChangeRequestTable ref={meterChangeRequestTableRef} showTable={false} />
                    <Reconnectiontable ref={reconnectionTableRef} showTable={false} />
                    <AdjustLastBillTable ref={adjustLastBillTableRef} showTable={false} />
                    <AmendBill2636Table ref={partPaymentTableRef} showTable={false} />
                </>
            )}
        </>
    )
}

export default SearchResultsPopup

